<script setup lang="ts">
import { AppTypography } from '@/components';
import type { Product } from '@/types/Product';

defineProps<{
  products: Product[];
}>();

const { isMobile } = useScreenSize();

const productPhoto = (product: Product) => {
  const photo = product.photos?.[0];
  return (isMobile.value ? photo?.thumb_url : photo?.url) || photo?.thumb_url || '/logo.png';
};

const merchantName = (product: Product) =>
  product.seller?.account?.business_name || product.seller?.full_name || 'Merchant';
</script>

<template>
  <div class="marketplace-products mt-6">
    <NuxtLink
      v-for="product in products"
      :key="product.id"
      :to="`/browse/products/${product.id}`"
      class="product-card"
    >
      <v-img
        class="product-card__photo"
        :aspect-ratio="1"
        :src="productPhoto(product)"
        :alt="product.title"
        cover
      />

      <div class="product-card__details">
        <AppTypography
          :type="isMobile ? 'footnote-2' : 'subheadline-1'"
          class="product-card__name text-truncate-multiline"
          element="span"
        >
          {{ product.title }}
        </AppTypography>

        <AppTypography
          :type="isMobile ? 'footnote-1' : 'subheadline-1'"
          class="product-card__price"
          element="span"
        >
          {{ product.formatted_price }}
        </AppTypography>

        <AppTypography
          :type="isMobile ? 'caption-1' : 'body-2'"
          color="-secondary"
          class="product-card__merchant text-truncate"
          element="span"
        >
          {{ merchantName(product) }}
        </AppTypography>
      </div>
    </NuxtLink>
  </div>
</template>

<style lang="scss" scoped>
.marketplace-products {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px 16px;
  }
}

.product-card {
  min-width: 0;
  color: inherit;
  text-decoration: none;

  &__photo {
    border: 0.5px solid rgb(var(--v-theme-stroke));
    border-radius: 10px;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 8px 4px 0;
  }

  &__name,
  &__price,
  &__merchant {
    display: block;
  }

  &__price {
    font-weight: 600;
  }
}
</style>
